import {z} from 'zod';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';
import {depositSchema} from '@dms/settings/shared';

const MAX_MARGIN = 100;

const generalSchema = z.object({
  name: z.string().nonempty(),
  serviceCaseTypeId: z.string().nonempty(),
  isDefaultForServiceCaseType: z.boolean().nullish(),
  serviceOrderTypeId: z.string().nonempty(),
  isUnitPriceWithVat: z.boolean().nullish(),
  priceType: z.string().nonempty(),
  priceTypeRatio: z.number(),
  minimalMaterialMargin: z
    .number()
    .max(MAX_MARGIN, i18n.t('entity.warehouse.validations.maxMargin', {max: MAX_MARGIN}))
    .nullish(),
  rateType: z.string().nonempty(),
  rateTypeRatio: z.number(),
  minimalWorkMargin: z
    .number()
    .max(MAX_MARGIN, i18n.t('entity.warehouse.validations.maxMargin', {max: MAX_MARGIN}))
    .nullish(),
  suffix: z.string().nonempty(),
  note: z.string().nullish(),
  defaultCustomerId: z.string().nullish(),
  isChangeableDefaultCustomer: z.boolean().nullish(),
  isCancelReasonRequired: z.boolean(),
});

const templateAndSeriesSchema = z.object({
  templateId: z.string().nullish(),
  docSeriesId: z.string().nullish(),
});

const templateOnlySchema = z.object({
  templateId: z.string().nullish(),
});

const requiredDocSeriesSchema = z.object({
  templateId: z.string().nullish(),
  docSeriesId: z.string().nonempty(),
});

const fastTrackIssueSchema = z.object({
  isEnable: z.boolean(),
  templateId: z.string().nullish(),
  docSeriesId: z.string().nullish(),
});

const validateFastTrackIssueFields = (
  data: z.infer<typeof fastTrackIssueSchema>,
  ctx: z.RefinementCtx
) => {
  if (!data.isEnable) {
    return;
  }

  const requiredMessage = i18n.t('errors.field_is_required');

  if (isNil(data.docSeriesId) || data.docSeriesId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['docSeriesId'],
      message: requiredMessage,
    });
  }
};

const fastTrackIssueSchemaWithValidation = fastTrackIssueSchema.superRefine(
  validateFastTrackIssueFields
);

const orderSchema = z.object({
  isUniqueDocSeries: z.boolean(),
  docSeriesId: z.string().nullish(),
});

const validateOrderFields = (data: z.infer<typeof orderSchema>, ctx: z.RefinementCtx) => {
  if (!data.isUniqueDocSeries) {
    return;
  }

  if (isNil(data.docSeriesId) || data.docSeriesId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['docSeriesId'],
      message: i18n.t('errors.field_is_required'),
    });
  }
};

const orderSchemaWithValidation = orderSchema.superRefine(validateOrderFields);

const documentsSchema = z.object({
  orderSheet: templateAndSeriesSchema.nullish(),
  orderOffer: templateAndSeriesSchema.nullish(),
  orderClaim: templateAndSeriesSchema.nullish(),
  orderHandlingProtocol: templateOnlySchema.nullish(),
  orderResolutionProtocol: templateOnlySchema.nullish(),
  serviceOrderIssue: requiredDocSeriesSchema,
  serviceOrderReturn: requiredDocSeriesSchema,
  fastTrackIssue: fastTrackIssueSchemaWithValidation,
  order: orderSchemaWithValidation,
});

const docSeriesSchema = z.object({
  docSeriesId: z.string().nullish(),
});

const invoiceSchema = z.object({
  templateId: z.string().nullish(),
  docSeriesId: z.string().nullish(),
});

const fullPaymentSchema = z.object({
  isAllowed: z.boolean(),
  isAutoInitiatePayment: z.boolean().nullish(),
  taxDocument: docSeriesSchema.nullish(),
  taxDocumentCorrection: docSeriesSchema.nullish(),
  deposit: docSeriesSchema.nullish(),
  invoice: invoiceSchema.nullish(),
  invoiceCorrection: docSeriesSchema.nullish(),
  invoiceEu: docSeriesSchema.nullish(),
  invoiceEuCorrection: docSeriesSchema.nullish(),
  invoiceOutsideEu: docSeriesSchema.nullish(),
  invoiceOutsideEuCorrection: docSeriesSchema.nullish(),
});

const cashPaymentSchema = fullPaymentSchema.extend({
  cashRegisterId: z.string().nullish(),
});

const internalPaymentSchema = z.object({
  isAllowed: z.boolean(),
  isAutoInitiatePayment: z.boolean().nullish(),
  invoice: invoiceSchema.nullish(),
  invoiceCorrection: docSeriesSchema.nullish(),
});

const validateFullPaymentFields = (
  data: z.infer<typeof fullPaymentSchema>,
  ctx: z.RefinementCtx
) => {
  if (!data.isAllowed) {
    return;
  }

  const requiredMessage = i18n.t('errors.field_is_required');

  if (isNil(data.invoice?.docSeriesId) || data.invoice?.docSeriesId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoice', 'docSeriesId'],
      message: requiredMessage,
    });
  }

  if (isNil(data.invoice?.templateId) || data.invoice?.templateId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoice', 'templateId'],
      message: requiredMessage,
    });
  }

  if (isNil(data.deposit?.docSeriesId) || data.deposit?.docSeriesId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['deposit', 'docSeriesId'],
      message: requiredMessage,
    });
  }

  if (isNil(data.taxDocument?.docSeriesId) || data.taxDocument?.docSeriesId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['taxDocument', 'docSeriesId'],
      message: requiredMessage,
    });
  }

  if (
    isNil(data.taxDocumentCorrection?.docSeriesId) ||
    data.taxDocumentCorrection?.docSeriesId === ''
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['taxDocumentCorrection', 'docSeriesId'],
      message: requiredMessage,
    });
  }

  if (isNil(data.invoiceCorrection?.docSeriesId) || data.invoiceCorrection?.docSeriesId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoiceCorrection', 'docSeriesId'],
      message: requiredMessage,
    });
  }

  if (isNil(data.invoiceEu?.docSeriesId) || data.invoiceEu?.docSeriesId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoiceEu', 'docSeriesId'],
      message: requiredMessage,
    });
  }

  if (
    isNil(data.invoiceEuCorrection?.docSeriesId) ||
    data.invoiceEuCorrection?.docSeriesId === ''
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoiceEuCorrection', 'docSeriesId'],
      message: requiredMessage,
    });
  }

  if (isNil(data.invoiceOutsideEu?.docSeriesId) || data.invoiceOutsideEu?.docSeriesId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoiceOutsideEu', 'docSeriesId'],
      message: requiredMessage,
    });
  }

  if (
    isNil(data.invoiceOutsideEuCorrection?.docSeriesId) ||
    data.invoiceOutsideEuCorrection?.docSeriesId === ''
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoiceOutsideEuCorrection', 'docSeriesId'],
      message: requiredMessage,
    });
  }
};

const validateInternalPaymentFields = (
  data: z.infer<typeof internalPaymentSchema>,
  ctx: z.RefinementCtx
) => {
  if (!data.isAllowed) {
    return;
  }

  const requiredMessage = i18n.t('errors.field_is_required');

  if (isNil(data.invoice?.docSeriesId) || data.invoice?.docSeriesId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoice', 'docSeriesId'],
      message: requiredMessage,
    });
  }

  if (isNil(data.invoice?.templateId) || data.invoice?.templateId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoice', 'templateId'],
      message: requiredMessage,
    });
  }

  if (isNil(data.invoiceCorrection?.docSeriesId) || data.invoiceCorrection?.docSeriesId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoiceCorrection', 'docSeriesId'],
      message: requiredMessage,
    });
  }
};

const validateCashPaymentFields = (
  data: z.infer<typeof cashPaymentSchema>,
  ctx: z.RefinementCtx
) => {
  validateFullPaymentFields(data, ctx);

  if (!data.isAllowed || !data.isAutoInitiatePayment) {
    return;
  }

  if (isNil(data.cashRegisterId) || data.cashRegisterId === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['cashRegisterId'],
      message: i18n.t('errors.field_is_required'),
    });
  }
};

const fullPaymentSchemaWithValidation = fullPaymentSchema.superRefine(validateFullPaymentFields);
const cashPaymentSchemaWithValidation = cashPaymentSchema.superRefine(validateCashPaymentFields);
const internalPaymentSchemaWithValidation = internalPaymentSchema.superRefine(
  validateInternalPaymentFields
);

const defaultPaymentTypeSchema = z.enum([
  'BANK_TRANSFER',
  'CARD',
  'CASH',
  'CASH_ON_DELIVERY',
  'INTERNAL',
]);

const paymentTypeSchema = z.object({
  defaultPaymentType: defaultPaymentTypeSchema.nullish(),
  cashPayment: cashPaymentSchemaWithValidation,
  cashOnDeliveryPayment: fullPaymentSchemaWithValidation,
  cardPayment: fullPaymentSchemaWithValidation,
  bankPayment: fullPaymentSchemaWithValidation,
  internalPayment: internalPaymentSchemaWithValidation,
});

const mandatoryFieldsSchema = z.record(z.string(), z.boolean().nullish());
const mandatoryFieldConditionsSchema = z.record(z.string(), z.array(z.string()).nullish());

const accountingSchema = z
  .object({
    fixedVatType: z.string().nullish(),
    excludedServiceCenterIds: z.array(z.string()),
    defaultServiceCenterId: z.string().nullish(),
    isDefaultServiceCenterLocked: z.boolean(),
    isTaxDeductible: z.string().nullish(),
  })
  .superRefine((data, ctx) => {
    if (data.isTaxDeductible === 'false' && isNil(data.fixedVatType)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('errors.field_is_required'),
        path: ['fixedVatType'],
      });
    }
  });

const reportingSchema = z.object({
  excludedServiceOrderGroupIds: z.array(z.string()),
  defaultServiceOrderGroupId: z.string().nullish(),
  isDefaultServiceOrderGroupLocked: z.boolean(),
  isPreDeliveryInspection: z.boolean(),
  isDbiReportBodyshop: z.boolean(),
});

const integrationSchema = z.object({
  isDefaultForXentryServiceMeasures: z.boolean(),
  isExcludedFromXentrySynchronization: z.boolean(),
  isAutomaticSendToChronos: z.boolean(),
});

export const ServiceOrderVariantsDetailFormSchema = z.object({
  general: generalSchema,
  documents: documentsSchema,
  paymentType: paymentTypeSchema,
  deposit: depositSchema,
  mandatoryFields: mandatoryFieldsSchema,
  mandatoryFieldConditions: mandatoryFieldConditionsSchema,
  accounting: accountingSchema,
  reporting: reportingSchema,
  integration: integrationSchema,
  warehousePriorities: z.array(z.string()).nullish(),
});

export type ServiceOrderVariantsDetailFormSchemaType = z.infer<
  typeof ServiceOrderVariantsDetailFormSchema
>;
